/**
 * Trashcan Utilities
 *
 * Functions for soft-deleting and restoring whole blocks and canvas elements.
 *
 * @module trashcan/trashcan-utils
 */

// Subpath, not the barrel: the surface barrel re-exports crud-extension.ts, which imports this module.
import {
  assertMindmapCompositionsFit,
  type CompositionElement,
  snapshotMapRecord,
} from '@blocksuite/affine-block-surface/mindmap-composition';
import type {
  BlockDeletionOptions,
  BlockDeletionOutcome,
  BlockDeletionResult,
} from '@blocksuite/affine-shared/commands';
import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import type { GfxController, GfxModel } from '@blocksuite/std/gfx';
import {
  type BlockModel,
  Slice,
  type Store,
  type Workspace,
} from '@blocksuite/store';
import {
  isDatabaseTrashBlocked,
  PAGE_TREE_DELETED_ID,
  PageStructureWriteError,
} from '@streamlain/database-identity';

import type {
  TrashcanBlockModel,
  TrashcanItem,
  TrashcanItemPosition,
} from '../schemas/trashcan-model.js';
import { peekSystemWorkspaceStore } from '../system-workspace/system-workspace-store.js';

// =============================================================================
// Constants
// =============================================================================

/** Block types that support soft-delete */
const TRASHABLE_FLAVOURS = [
  'affine:database',
  'affine:automation',
  'affine:elster',
] as const;

/** 30 days in milliseconds */
/** How long an entry stays in the trash before the cleanup deletes it. */
export const TRASH_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

// =============================================================================
// Ghost Position Detection
// =============================================================================

interface GhostPosition {
  centerX: number;
  centerY: number;
}

/**
 * Find a ghost block that points to the given trashed item.
 * Returns the center position of the ghost if found.
 *
 * This allows restoring blocks at the position where the user
 * placed the ghost, rather than at the viewport center.
 */
function findGhostPositionForItem(
  store: Store,
  itemId: string
): GhostPosition | null {
  const ghosts = store.getBlocksByFlavour('affine:ghost');

  for (const ghostBlock of ghosts) {
    const props = ghostBlock.model?.props as {
      targetDatabaseId?: string;
      xywh?: string;
    };

    // Check if this ghost points to the item being restored
    if (props?.targetDatabaseId === itemId && props?.xywh) {
      const xywh = parseXYWH(props.xywh);
      if (!xywh) continue;
      const [x, y, w, h] = xywh;
      return {
        centerX: x + w / 2,
        centerY: y + h / 2,
      };
    }
  }

  return null;
}

// =============================================================================
// Trashcan Access
// =============================================================================

/**
 * Get or create the trashcan block in the document
 */
export function getOrCreateTrashcan(store: Store): TrashcanBlockModel | null {
  // Find existing trashcan
  const existing = store.getBlocksByFlavour('affine:trashcan')[0];
  if (existing) {
    return existing.model as TrashcanBlockModel;
  }

  // Create new trashcan under root
  const rootId = store.root?.id;
  if (!rootId) {
    console.warn('Cannot create trashcan: no root block');
    return null;
  }

  try {
    const id = store.addBlock('affine:trashcan', { items: [] }, rootId);
    const block = store.getBlock(id);
    return block?.model as TrashcanBlockModel;
  } catch (e) {
    console.error('Failed to create trashcan block:', e);
    return null;
  }
}

/**
 * Get the trashcan block if it exists
 */
export function getTrashcan(store: Store): TrashcanBlockModel | null {
  const existing = store.getBlocksByFlavour('affine:trashcan')[0];
  return (existing?.model as TrashcanBlockModel) ?? null;
}

// =============================================================================
// Soft Delete
// =============================================================================

/**
 * Check if a block type supports soft-delete
 */
export function isTrashable(flavour: string): boolean {
  return TRASHABLE_FLAVOURS.includes(
    flavour as (typeof TRASHABLE_FLAVOURS)[number]
  );
}

/** Blocks that only hold other blocks: empty when their children are. */
const CONTAINER_FLAVOURS = new Set(['affine:note']);

/**
 * Typed and deleted text is no deleted block: an empty text block (or a note
 * holding only such blocks) leaves no trash entry.
 */
function hasContent(model: BlockModel): boolean {
  if (model.children.some(hasContent)) return true;
  const text = (model as { text?: { length: number } }).text;
  if (text) return text.length > 0;
  return !CONTAINER_FLAVOURS.has(model.flavour);
}

function positionOf(
  store: Store,
  model: BlockModel
): TrashcanItemPosition | undefined {
  const parent = store.getParent(model);
  if (!parent) return undefined;
  return {
    parentId: parent.id,
    previousSiblingId: store.getPrev(model)?.id ?? null,
  };
}

/**
 * Write the entry and remove the original in one transaction, so one undo
 * takes back both. The trashcan block is created before: a block added inside
 * a transaction is not readable until it ends.
 */
function recordInTrashcan(
  store: Store,
  item: TrashcanItem,
  remove: () => void
): boolean {
  const trashcan = getOrCreateTrashcan(store);
  if (!trashcan) {
    console.warn('Could not get/create trashcan');
    return false;
  }
  // One entry more, not the list written anew: the doc grows by the entry alone.
  store.transact(() => {
    trashcan.props.items.push(item);
    remove();
  });
  return true;
}

/**
 * Drops the entry `id` alone; the other entries stay as they are in the doc. In a transaction of
 * its own, the trash list reads the shortened entries when it hears of the change.
 */
function dropTrashcanEntry(
  store: Store,
  trashcan: TrashcanBlockModel,
  id: string,
  undoable = true
): void {
  const index = trashcan.props.items.findIndex(entry => entry.id === id);
  if (index < 0) return;
  store.transact(() => trashcan.props.items.splice(index, 1), undoable);
}

function dispatchTrashed(store: Store, item: TrashcanItem): void {
  // Notify the app layer of a move made in the doc (toast with Undo / Open
  // trash). Event-based so this blocksuite package keeps no dependency on app
  // services or i18n.
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('streamlain:block-trashed', {
      detail: {
        blockId: item.id,
        blockType: item.blockType,
        displayName: item.displayName,
        docId: store.id,
      },
    })
  );
}

/** Put an ordinary block into the trash with its old place. */
function trashOrdinaryBlock(
  store: Store,
  model: BlockModel,
  options: BlockDeletionOptions
): BlockDeletionOutcome {
  const snapshot = store
    .getTransformer()
    .sliceToSnapshot(Slice.fromModels(store, [model]));
  if (!snapshot) {
    console.error('[Trashcan] Failed to create block snapshot:', model.id);
    return 'rejected';
  }
  const item: TrashcanItem = {
    id: model.id,
    blockType: 'block',
    kind: model.flavour,
    snapshot,
    deletedAt: Date.now(),
    deletedBy: options.deletedBy,
    displayName: getBlockDisplayName(model) || undefined,
    position: positionOf(store, model),
    connectors: options.connectors,
    groups: options.groups,
  };
  if (!recordInTrashcan(store, item, () => store.deleteBlock(model))) {
    return 'rejected';
  }
  dispatchTrashed(store, item);
  return 'deleted';
}

/**
 * The single policy for user deletion of a whole block. A cancelled or failed
 * trash request must never fall through to a permanent deletion.
 */
export function deleteBlockWithTrashcan(
  store: Store,
  model: BlockModel,
  options: BlockDeletionOptions = {}
): BlockDeletionResult {
  if (store.readonly || model.flavour === 'affine:ghost') {
    return 'cancelled';
  }
  if (!store.getBlock(model.id)) return 'cancelled';
  if (isTrashable(model.flavour)) {
    // A queued server trash is accepted: the block stays until the receipt
    // syncs, the pending presentation stands in for it meanwhile.
    return requestMoveToTrashcan(store, model.id, options)
      .then(outcome =>
        outcome === 'moved' || outcome === 'queued'
          ? ('deleted' as const)
          : outcome
      )
      .catch(error => {
        console.error('[Trashcan] Failed to delete block:', error);
        return 'rejected' as const;
      });
  }
  if (options.moving || !hasContent(model)) {
    store.deleteBlock(model);
    return 'deleted';
  }
  return trashOrdinaryBlock(store, model, options);
}

/** A canvas element as the trash sees it (no dependency on the surface package). */
export interface TrashableCanvasElement {
  id: string;
  type: string;
  serialize(): Record<string, unknown>;
  /** A mind map's nodes: they leave and come back with its one entry. */
  childElements?: readonly object[];
}

function isTrashableNode(
  node: object
): node is Pick<TrashableCanvasElement, 'id' | 'serialize'> {
  return (
    'id' in node && 'serialize' in node && typeof node.serialize === 'function'
  );
}

/**
 * The policy for user deletion of a canvas element (shape, text, connector,
 * brush stroke): same rules as {@link deleteBlockWithTrashcan}.
 */
export function deleteElementWithTrashcan(
  store: Store,
  surface: { deleteElement(id: string): void },
  element: TrashableCanvasElement,
  options: BlockDeletionOptions = {}
): BlockDeletionOutcome {
  if (store.readonly) return 'cancelled';
  const snapshot = element.serialize();
  const text = elementText(snapshot);
  if (options.moving || (element.type === 'text' && !text)) {
    surface.deleteElement(element.id);
    return 'deleted';
  }
  const nodes =
    element.type === 'mindmap'
      ? (element.childElements ?? []).map(node => {
          if (!isTrashableNode(node))
            throw new PageStructureWriteError(
              'Mindmap node is not a canvas element'
            );
          return node;
        })
      : [];
  const item: TrashcanItem = {
    id: element.id,
    blockType: 'element',
    kind: element.type,
    snapshot,
    ...(element.type === 'mindmap'
      ? { members: nodes.map(node => node.serialize()) }
      : {}),
    deletedAt: Date.now(),
    deletedBy: options.deletedBy,
    displayName: text || undefined,
    connectors: options.connectors,
    groups: options.groups,
  };
  const remove = () => {
    surface.deleteElement(element.id);
    for (const node of nodes) surface.deleteElement(node.id);
  };
  if (!recordInTrashcan(store, item, remove)) {
    return 'rejected';
  }
  dispatchTrashed(store, item);
  return 'deleted';
}

function elementText(snapshot: Record<string, unknown>): string {
  const text = snapshot.text;
  return typeof text === 'string' ? text.trim() : '';
}

/**
 * Sharded database identity carried into the trashcan so discovery and
 * permanent delete can address the storage documents after the host block is
 * gone.
 */
export interface DatabaseTrashcanIdentity {
  databaseId: string;
  databaseRootDocId?: string;
}

/**
 * Read the sharded database identity from a host database block model.
 */
export function readDatabaseTrashcanIdentity(
  model: unknown
): DatabaseTrashcanIdentity {
  const m = model as {
    id: string;
    props?: { databaseId?: unknown; databaseRootDocId?: unknown };
  };
  const databaseId =
    typeof m.props?.databaseId === 'string' && m.props.databaseId.trim()
      ? m.props.databaseId
      : m.id;
  const databaseRootDocId =
    typeof m.props?.databaseRootDocId === 'string' &&
    m.props.databaseRootDocId.trim()
      ? m.props.databaseRootDocId
      : undefined;
  return { databaseId, databaseRootDocId };
}

/**
 * Resolve the user-facing title of a sharded database from its root system
 * document. The host block only carries a provisioned placeholder title; the
 * real title lives on the storage block inside the root document.
 *
 * Returns `undefined` when the root document is not registered in the
 * workspace, when the database keeps its storage inline in the host document,
 * or when the storage block has no non-empty title.
 */
export function resolveDatabaseRootTitle(
  hostStore: Store,
  identity: DatabaseTrashcanIdentity
): string | undefined {
  const rootDocId = identity.databaseRootDocId;
  if (!rootDocId || rootDocId === hostStore.id) return undefined;
  const rootStore = peekSystemWorkspaceStore(hostStore, rootDocId);
  const storageModel = rootStore?.getBlock(identity.databaseId)?.model;
  if (!storageModel || storageModel.flavour !== 'affine:database') {
    return undefined;
  }
  const title = (
    storageModel as { props?: { title?: { toString(): string } } }
  ).props?.title
    ?.toString()
    .trim();
  return title ? title : undefined;
}

/**
 * What the app layer decided for a table or automation the user deletes:
 * - `trash-locally`: confirmed; the block moves to the doc's own trash.
 * - `handed-to-server`: confirmed and queued for the server's trash; the doc
 *   changes only by the sync of the server's receipt.
 * - `cancelled`: the user declined.
 */
export type BlockTrashGuardDecision =
  | 'trash-locally'
  | 'handed-to-server'
  | 'cancelled';

/** What the guard of each guarded block kind is asked with. */
export type BlockTrashGuardRequests = {
  database: {
    store: Store;
    blockId: string;
    identity: DatabaseTrashcanIdentity;
    displayName: string;
  };
  automation: { store: Store; blockId: string; displayName: string };
};

export type GuardedTrashBlockKind = keyof BlockTrashGuardRequests;

/**
 * Guard consulted before a block of its kind is soft-deleted through
 * {@link requestMoveToTrashcan}. The app layer registers one per workspace
 * and kind; it confirms with the user and decides where the trash happens. A
 * protected table never reaches it.
 */
export type BlockTrashGuard<Kind extends GuardedTrashBlockKind> = (
  request: BlockTrashGuardRequests[Kind]
) => Promise<BlockTrashGuardDecision>;

type WorkspaceTrashGuards = {
  [Kind in GuardedTrashBlockKind]?: BlockTrashGuard<Kind>;
};

const blockTrashGuards = new WeakMap<Workspace, WorkspaceTrashGuards>();

/**
 * Register (or clear with `null`) the trash guard of a block kind in a
 * workspace. A store consults the guards of its own workspace.
 */
export function setBlockTrashGuard<Kind extends GuardedTrashBlockKind>(
  workspace: Workspace,
  kind: Kind,
  guard: BlockTrashGuard<Kind> | null
): void {
  const guards: WorkspaceTrashGuards = {
    ...blockTrashGuards.get(workspace),
    [kind]: guard ?? undefined,
  };
  blockTrashGuards.set(workspace, guards);
}

/**
 * Outcome of {@link requestMoveToTrashcan}:
 * - `moved`: the block is in the trashcan.
 * - `queued`: the server's trash is queued; the block stays until its receipt.
 * - `cancelled`: the user declined, or the local block is protected/read-only.
 * - `rejected`: the block is not trashable or the trashcan write failed.
 */
export type TrashcanMoveOutcome = 'moved' | 'queued' | 'cancelled' | 'rejected';

/**
 * Soft-delete a block after consulting the guard of its flavour (database,
 * automation).
 *
 * Other blocks and flavours without a registered guard are trashed
 * immediately.
 */
const pendingTrashRequests = new WeakMap<
  Store,
  Map<string, Promise<TrashcanMoveOutcome>>
>();

export function requestMoveToTrashcan(
  store: Store,
  blockId: string,
  options: BlockDeletionOptions = {}
): Promise<TrashcanMoveOutcome> {
  let pending = pendingTrashRequests.get(store);
  if (!pending) {
    pending = new Map();
    pendingTrashRequests.set(store, pending);
  }
  const existing = pending.get(blockId);
  if (existing) return existing;
  const request = confirmAndMoveToTrashcan(store, blockId, options).finally(
    () => {
      pending.delete(blockId);
    }
  );
  pending.set(blockId, request);
  return request;
}

async function confirmAndMoveToTrashcan(
  store: Store,
  blockId: string,
  options: BlockDeletionOptions
): Promise<TrashcanMoveOutcome> {
  const block = store.getBlock(blockId);
  if (!block) return 'rejected';
  const isBlocked = () => {
    if (store.readonly) return true;
    if (block.model.flavour !== 'affine:database') return false;
    return isDatabaseTrashBlocked(block.model.props);
  };
  if (isBlocked()) return 'cancelled';
  // An automation leaves only through its guard: the app decides between the
  // server trash (cloud) and the doc (local). Without one, a doc-only move
  // would leave the config active on the server, so nothing moves.
  if (
    block.model.flavour === 'affine:automation' &&
    !hasAutomationGuard(store)
  ) {
    return 'cancelled';
  }
  const decide = trashGuardFor(store, blockId, block.model);
  if (decide) {
    const decision = await decide();
    if (decision === 'handed-to-server') return 'queued';
    if (decision === 'cancelled' || isBlocked()) return 'cancelled';
    // Another local actor may have removed it while confirmation was open.
    if (!store.getBlock(blockId)) return 'cancelled';
  }
  return moveToTrashcan(store, blockId, options) ? 'moved' : 'rejected';
}

function hasAutomationGuard(store: Store): boolean {
  return Boolean(blockTrashGuards.get(store.workspace)?.automation);
}

function trashGuardFor(
  store: Store,
  blockId: string,
  model: BlockModel
): (() => Promise<BlockTrashGuardDecision>) | null {
  const guards = blockTrashGuards.get(store.workspace);
  if (model.flavour === 'affine:database' && guards?.database) {
    const guard = guards.database;
    const identity = readDatabaseTrashcanIdentity(model);
    const displayName =
      resolveDatabaseRootTitle(store, identity) ?? getBlockDisplayName(model);
    return () => guard({ store, blockId, identity, displayName });
  }
  if (model.flavour === 'affine:automation' && guards?.automation) {
    const guard = guards.automation;
    const displayName = getBlockDisplayName(model);
    return () => guard({ store, blockId, displayName });
  }
  return null;
}

/**
 * Move a block to the trashcan (soft-delete)
 *
 * @returns true if handled, false if should be deleted normally
 */
export function moveToTrashcan(
  store: Store,
  blockId: string,
  options: BlockDeletionOptions = {}
): boolean {
  const block = store.getBlock(blockId);
  if (!block) {
    console.warn('Block not found:', blockId);
    return false;
  }

  const flavour = block.model?.flavour;
  if (!flavour || !isTrashable(flavour)) {
    return false;
  }

  if (
    flavour === 'affine:database' &&
    isDatabaseTrashBlocked(block.model.props)
  ) {
    console.warn('The system protects this database:', blockId);
    return true;
  }

  try {
    // Create snapshot of the block
    const slice = Slice.fromModels(store, [block.model]);
    const transformer = store.getTransformer();
    const snapshot = transformer.sliceToSnapshot(slice);

    if (!snapshot) {
      console.error('Failed to create block snapshot');
      return false;
    }

    const databaseIdentity =
      flavour === 'affine:database'
        ? readDatabaseTrashcanIdentity(block.model)
        : null;
    const displayName =
      (databaseIdentity && resolveDatabaseRootTitle(store, databaseIdentity)) ||
      getBlockDisplayName(block.model);

    // Add to trashcan
    const blockTypeMap: Record<string, TrashcanItem['blockType']> = {
      'affine:database': 'database',
      'affine:automation': 'automation',
      'affine:elster': 'elster',
    };
    const item: TrashcanItem = {
      id: blockId,
      blockType: blockTypeMap[flavour] ?? 'automation',
      snapshot,
      deletedAt: Date.now(),
      deletedBy: options.deletedBy,
      displayName,
      connectors: options.connectors,
      groups: options.groups,
      databaseId: databaseIdentity?.databaseId,
      databaseRootDocId: databaseIdentity?.databaseRootDocId,
    };

    if (!recordInTrashcan(store, item, () => store.deleteBlock(block.model))) {
      return false;
    }
    // Fires only on a successful user-initiated soft-delete — not on
    // protected/file-index no-ops or programmatic block removals.
    dispatchTrashed(store, item);

    console.log('Moved block to trashcan:', blockId, displayName);
    return true;
  } catch (e) {
    console.error('Failed to move block to trashcan:', e);
    return false;
  }
}

/**
 * Get display name for a block
 */
/**
 * What the trash calls a block: its title, its text, or for a container
 * (note, frame) the first text inside it. Empty when there is none: the panel
 * then names the block by its kind.
 */
function getBlockDisplayName(model: unknown): string {
  const m = model as {
    props?: { title?: { toString(): string } };
    text?: { toString(): string };
    children?: unknown[];
  };

  // Try title (automation + database)
  if (m.props?.title?.toString) {
    const title = m.props.title.toString();
    if (title.trim()) return title;
  }

  // Text blocks: their text
  const text = m.text?.toString().trim();
  if (text) return text.length > 80 ? `${text.slice(0, 80)}…` : text;

  for (const child of m.children ?? []) {
    const name = getBlockDisplayName(child);
    if (name) return name;
  }
  return '';
}

// =============================================================================
// Restore
// =============================================================================

/**
 * Restore a block from the trashcan
 *
 * @param store - The document store
 * @param gfx - The GFX controller (for viewport center)
 * @param itemId - The ID of the trashcan item to restore
 * @returns The new block ID, or null if failed
 */
export async function restoreFromTrashcan(
  store: Store,
  gfx: GfxController,
  itemId: string
): Promise<string | null> {
  const trashcan = getTrashcan(store);
  if (!trashcan) {
    console.warn('Trashcan not found');
    return null;
  }

  const item = trashcan.getItem(itemId);
  if (!item) {
    console.warn('Item not found in trashcan:', itemId);
    return null;
  }
  try {
    const ends = (item.connectors ?? []).flatMap(connector =>
      [connector.source, connector.target].flatMap(end =>
        end &&
        typeof end === 'object' &&
        'id' in end &&
        typeof end.id === 'string'
          ? [end.id]
          : []
      )
    );
    await store.pageStructure.loadMissingPartsOf(ends);

    if (item.blockType === 'element') {
      return restoreElement(store, gfx, trashcan, item);
    }
    const target =
      item.blockType === 'block'
        ? restoreTarget(store, item)
        : { parent: store.root?.id, index: undefined };
    // Deserialize snapshot
    const transformer = store.getTransformer();
    const slice = await transformer.snapshotToSlice(
      item.snapshot as Parameters<typeof transformer.snapshotToSlice>[0],
      store,
      target.parent,
      target.index
    );

    if (!slice || slice.content.length === 0) {
      console.error('Failed to restore from snapshot');
      return null;
    }

    // Try to find a ghost that points to this trashed item
    // If found, use the ghost's center position for the restored block
    let centerX: number;
    let centerY: number;
    const ghostPosition = findGhostPositionForItem(store, itemId);

    if (ghostPosition) {
      centerX = ghostPosition.centerX;
      centerY = ghostPosition.centerY;
      console.log(
        `[Trashcan] Using ghost position for restored block: (${centerX}, ${centerY})`
      );
    } else {
      // Fallback to viewport center
      const viewport = gfx.viewport;
      centerX = viewport.centerX;
      centerY = viewport.centerY;
    }

    // The restored block should be the first in the slice
    const restoredModel = slice.content[0];
    if (!restoredModel) {
      console.warn('Restored slice has no content');
      return null;
    }
    const newBlockId = restoredModel.id;
    if (newBlockId !== item.id) {
      const importedModel = store.getBlock(newBlockId)?.model;
      if (importedModel) {
        store.deleteBlock(importedModel);
      }
      console.error(
        `[Trashcan] Restore changed block id from "${item.id}" to "${newBlockId}".`
      );
      return null;
    }

    // Database, automation and ELSTER come back at their ghost or the
    // viewport centre; every other block keeps its own place.
    // Note: restoredModel is a DraftModel, we need to get the actual BlockModel
    const actualModel = store.getBlock(newBlockId)?.model;
    if (
      item.blockType !== 'block' &&
      actualModel &&
      'xywh' in actualModel.props
    ) {
      const xywh = parseXYWH(actualModel.props.xywh);
      if (!xywh) {
        return null;
      }
      const [, , w, h] = xywh;
      const newXywh = serializeXYWH(centerX - w / 2, centerY - h / 2, w, h);
      store.updateBlock(newBlockId, { xywh: newXywh });
    }

    try {
      writeRestore(store, gfx.surface, add => {
        finishRestore(store, gfx, trashcan, item, newBlockId, add);
        return newBlockId;
      });
    } catch (error) {
      // The entry stays; a retry restores the block under the same id.
      const restored = store.getBlock(newBlockId)?.model;
      if (restored) store.deleteBlock(restored);
      throw error;
    }
    console.log('Restored block from trashcan:', newBlockId);
    return newBlockId;
  } catch (e) {
    console.error('Failed to restore from trashcan:', e);
    return null;
  }
}

/**
 * Bring several entries back, the last deleted first: each returns to the
 * place it had when it left, so neighbours deleted one after another line up
 * again as they stood. `onProgress` hears after every entry.
 */
export async function restoreManyFromTrashcan(
  store: Store,
  gfx: GfxController,
  itemIds: readonly string[],
  onProgress?: (done: number) => void
): Promise<{ restored: number; failed: number }> {
  const wanted = new Set(itemIds);
  // The trash keeps its entries in the order they left; the clock can give
  // two deletions the same millisecond.
  const ordered = (getTrashcan(store)?.props.items ?? [])
    .filter(item => wanted.has(item.id))
    .reverse();
  let restored = 0;
  for (const [index, item] of ordered.entries()) {
    if (await restoreFromTrashcan(store, gfx, item.id)) restored++;
    onProgress?.(index + 1);
  }
  return { restored, failed: ordered.length - restored };
}

/**
 * Where a deleted block goes back: after its old neighbour under its old
 * parent; the parent's end when the neighbour is gone; the end of the last
 * note when the parent is gone.
 */
function restoreTarget(
  store: Store,
  item: TrashcanItem
): { parent?: string; index?: number } {
  const position = item.position;
  const parent = position && store.getBlock(position.parentId)?.model;
  if (position && parent) {
    if (position.previousSiblingId === null) {
      return { parent: parent.id, index: 0 };
    }
    const previous = parent.children.findIndex(
      child => child.id === position.previousSiblingId
    );
    return {
      parent: parent.id,
      index: previous >= 0 ? previous + 1 : parent.children.length,
    };
  }
  const lastNote = store.getBlocksByFlavour('affine:note').at(-1)?.model;
  return lastNote
    ? { parent: lastNote.id, index: lastNote.children.length }
    : { parent: store.root?.id };
}

function restoreElement(
  store: Store,
  gfx: GfxController,
  trashcan: TrashcanBlockModel,
  item: TrashcanItem
): string | null {
  const surface = gfx.surface;
  if (!surface) {
    console.warn('[Trashcan] No surface to restore a canvas element into');
    return null;
  }
  if (item.kind === 'mindmap') {
    // The entry carries the nodes: they come back as new elements in one part.
    const {
      id: _mapId,
      children,
      ...mapProps
    } = item.snapshot as Record<string, unknown>;
    const members = (item.members ?? []) as CompositionElement[];
    const details = snapshotMapRecord(children);
    const parentOf = (detail: unknown) =>
      (detail as { parent?: string }).parent;
    if (
      Object.keys(details).some(id => !members.some(node => node.id === id)) ||
      Object.values(details).some(
        detail =>
          parentOf(detail) !== undefined && !(parentOf(detail)! in details)
      )
    )
      throw new PageStructureWriteError('Missing mindmap node in trash entry');
    assertMindmapCompositionsFit(store, surface.id, [
      { mindmap: item.snapshot as CompositionElement, members },
    ]);
    return writeRestore(store, surface, add => {
      const renamed = new Map<string, string>();
      let first: string | undefined;
      for (const { id, ...props } of members) {
        const created = add(
          props as { type: string },
          first ? { partOf: first } : undefined
        );
        first ??= created;
        renamed.set(id, created);
      }
      const record: Record<string, unknown> = {};
      for (const [id, detail] of Object.entries(details)) {
        const { parent, ...rest } = detail as { parent?: string };
        record[renamed.get(id)!] = parent
          ? { ...rest, parent: renamed.get(parent) }
          : rest;
      }
      const mapInput: Record<string, unknown> = {
        ...mapProps,
        children: record,
      };
      const newId = add(mapInput as { type: string });
      finishRestore(store, gfx, trashcan, item, newId, add, renamed);
      return newId;
    });
  }
  const { id: _oldId, ...props } = item.snapshot as Record<string, unknown>;
  return writeRestore(store, surface, add => {
    const newId = add(props as { type: string });
    finishRestore(store, gfx, trashcan, item, newId, add);
    return newId;
  });
}

/**
 * Hands each connector that could not come back to the entry that holds its
 * missing end (the element or a mind map node of it), with the ends already
 * re-pointed to the restored ids: restoring that entry brings it back.
 */
function handOverTrashedConnectors(
  trashcan: TrashcanBlockModel,
  restoredItemId: string,
  waiting: ReadonlyMap<
    string,
    { props: Record<string, unknown>; missing: string[] }
  >
): void {
  const items = trashcan.props.items;
  const holds = (entry: TrashcanItem, id: string) =>
    entry.id === id || !!entry.members?.some(node => node.id === id);
  for (const [connectorId, { props, missing }] of waiting) {
    const index = items.findIndex(
      entry =>
        entry.id !== restoredItemId && missing.some(id => holds(entry, id))
    );
    if (index < 0) continue;
    const entry = items[index];
    items.splice(index, 1, {
      ...entry,
      connectors: [
        ...(entry.connectors ?? []).filter(c => c.id !== connectorId),
        { ...props, id: connectorId },
      ],
    });
  }
}

function repointTrashedConnectors(
  trashcan: TrashcanBlockModel,
  restoredItemId: string,
  ids: ReadonlyMap<string, string>
): void {
  const items = trashcan.props.items;
  const repoint = (end: unknown) => {
    const id = (end as { id?: string } | undefined)?.id;
    const next = id ? ids.get(id) : undefined;
    return next !== undefined && next !== id
      ? { ...(end as object), id: next }
      : end;
  };
  items.forEach((entry, index) => {
    if (entry.id === restoredItemId || !entry.connectors) return;
    const touches = entry.connectors.some(c =>
      [c.source, c.target].some(end => repoint(end) !== end)
    );
    if (!touches) return;
    items.splice(index, 1, {
      ...entry,
      connectors: entry.connectors.map(c => ({
        ...c,
        source: repoint(c.source),
        target: repoint(c.target),
      })),
    });
  });
}

function liveEntry(store: Store, id: string): boolean {
  const entry = store.pageStructure.entry(id);
  return !!entry && entry.p !== PAGE_TREE_DELETED_ID && entry.d === undefined;
}

type RestoreAdd = (
  props: { type: string },
  unit?: { partOf: string }
) => string;

/**
 * Writes a restore in one transaction. A failure removes what it already
 * wrote, so the entry stays and a retry does not duplicate elements.
 */
function writeRestore(
  store: Store,
  surface: GfxController['surface'],
  write: (add: RestoreAdd) => string
): string {
  const created: string[] = [];
  const add: RestoreAdd = (props, unit) => {
    if (!surface)
      throw new PageStructureWriteError('No surface to restore elements into');
    const id = surface.addElement(props, unit);
    created.push(id);
    return id;
  };
  let restored = '';
  store.transact(() => {
    try {
      restored = write(add);
    } catch (error) {
      for (const id of created.reverse()) surface?.deleteElement(id);
      throw error;
    }
  });
  return restored;
}

/**
 * Bring back the connectors that left with the item (their ends re-pointed to
 * the restored id), put the item and those connectors back into the groups
 * they left when a group still stands, drop the entry and tell the app layer.
 * A connector whose other end no longer exists stays out.
 */
function finishRestore(
  store: Store,
  gfx: GfxController,
  trashcan: TrashcanBlockModel,
  item: TrashcanItem,
  restoredId: string,
  add: RestoreAdd,
  renamed: ReadonlyMap<string, string> = new Map()
): void {
  const surface = gfx.surface;
  const exists = (id: string) =>
    id === restoredId ||
    [...renamed.values()].includes(id) ||
    store.hasBlock(id) ||
    surface?.hasElementById(id) === true ||
    // An element of an unloaded part is still there.
    liveEntry(store, id);
  const restoredIds = new Map([[item.id, restoredId], ...renamed]);
  // Connectors whose other end is in the trash too, with this item's ends re-pointed.
  const waiting = new Map<
    string,
    { props: Record<string, unknown>; missing: string[] }
  >();
  for (const connector of item.connectors ?? []) {
    if (!surface) break;
    const { id: connectorId, ...props } = connector;
    const ends = (['source', 'target'] as const).map(key => {
      const end = props[key] as { id?: string } | undefined;
      if (end?.id === item.id) props[key] = { ...end, id: restoredId };
      else if (end?.id && renamed.has(end.id))
        props[key] = { ...end, id: renamed.get(end.id) };
      return (props[key] as { id?: string } | undefined)?.id;
    });
    if (ends.every(id => id === undefined || exists(id))) {
      const id = add(props as { type: string });
      if (typeof connectorId === 'string') restoredIds.set(connectorId, id);
    } else if (typeof connectorId === 'string') {
      waiting.set(connectorId, {
        props,
        missing: ends.filter((id): id is string => !!id && !exists(id)),
      });
    }
  }
  for (const [memberId, groupId] of Object.entries(item.groups ?? {})) {
    const group = surface?.getGroupById(groupId);
    const id = restoredIds.get(memberId);
    const member = id
      ? (surface?.getElementById(id) ?? store.getBlock(id)?.model)
      : undefined;
    // Only real groups, like the server side: frames and mind maps keep
    // their own membership.
    if (group && 'type' in group && group.type === 'group' && member) {
      group.addChild(member as GfxModel);
    }
  }
  // Other entries' connectors now point at the new ids; the entry of a
  // missing end brings a waiting connector back.
  repointTrashedConnectors(
    trashcan,
    item.id,
    new Map([[item.id, restoredId], ...renamed])
  );
  if (waiting.size) handOverTrashedConnectors(trashcan, item.id, waiting);
  dropTrashcanEntry(store, trashcan, item.id);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('streamlain:block-restored', {
        detail: {
          blockId: restoredId,
          blockType: item.blockType,
          docId: store.id,
        },
      })
    );
  }
}

// =============================================================================
// Permanent Delete
// =============================================================================

/**
 * Permanently delete an item from the trashcan. Outside the undo history: the
 * server side (automation config) is gone with it, so it cannot come back.
 */
export function permanentDeleteFromTrashcan(
  store: Store,
  itemId: string
): boolean {
  if (store.readonly) return false;
  const trashcan = getTrashcan(store);
  if (!trashcan) {
    console.warn('Trashcan not found');
    return false;
  }

  const itemExists = trashcan.getItem(itemId);
  if (!itemExists) {
    console.warn('Item not found in trashcan:', itemId);
    return false;
  }
  // Outside the undo history: the server side is gone with it.
  dropTrashcanEntry(store, trashcan, itemId, false);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('streamlain:block-permanently-deleted', {
        detail: {
          blockId: itemId,
          blockType: itemExists.blockType,
          docId: store.id,
        },
      })
    );
  }

  console.log('Permanently deleted from trashcan:', itemId);
  return true;
}

// =============================================================================
// Cleanup
// =============================================================================

/**
 * The entries older than 30 days. The app deletes them when a member who may
 * edit opens the doc.
 */
export function findExpiredTrashItems(
  store: Store,
  now = Date.now()
): TrashcanItem[] {
  return (getTrashcan(store)?.props.items ?? []).filter(
    item => now - item.deletedAt >= TRASH_RETENTION_MS
  );
}
